import type { TablePaginationConfig } from 'antd/es/table';
import {
    isCanceledRequest,
    useCancelableRequest,
} from 'mixins/hooks/useCancelableRequest';
import { useCallback, useEffect, useState } from 'react';
import { getAdminTokenRequestList } from '../actions';
import {
    ApiTokenRequestDto,
    ApiTokenRequestJournalType,
    ApiTokenRequestListParams,
} from '../types';
import {
    DEFAULT_PAGE_SIZE,
    TOKEN_REQUEST_CREATED_SORT,
    TOKEN_REQUEST_ISSUED_SORT,
} from '../utils/constants';

export interface AppliedTokenRequestFilters {
    search?: string;
    status?: ApiTokenRequestListParams['status'];
    dateFrom?: string;
    dateTo?: string;
}

export const useTokenRequests = (journalType: ApiTokenRequestJournalType) => {
    const {
        createRequestSignal,
        finishRequest,
    } = useCancelableRequest();
    const [data, setData] = useState<ApiTokenRequestDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState<AppliedTokenRequestFilters>({});
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: DEFAULT_PAGE_SIZE,
    });

    const fetchData = useCallback(() => {
        const signal = createRequestSignal();
        const isIssuedJournal = journalType === 'ISSUED';

        setIsLoading(true);

        const params: ApiTokenRequestListParams = {
            ...filters,
            page: (pagination.current || 1) - 1,
            size: pagination.pageSize || DEFAULT_PAGE_SIZE,
            sort: isIssuedJournal
                ? TOKEN_REQUEST_ISSUED_SORT
                : TOKEN_REQUEST_CREATED_SORT,
            ...(isIssuedJournal ? { status: 'TOKEN_ISSUED' } : {}),
        };

        return getAdminTokenRequestList(params, signal)
            .then((response) => {
                setData(response.data.content || []);
                setPagination((current) => ({
                    ...current,
                    total: response.data.totalElements || 0,
                }));
            })
            .catch((error) => {
                if (!isCanceledRequest(error)) {
                    throw error;
                }
            })
            .finally(() => {
                if (finishRequest(signal)) {
                    setIsLoading(false);
                }
            });
    }, [
        createRequestSignal,
        filters,
        finishRequest,
        journalType,
        pagination.current,
        pagination.pageSize,
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const applyFilters = (nextFilters: AppliedTokenRequestFilters) => {
        setPagination((current) => ({ ...current, current: 1 }));
        setFilters(nextFilters);
    };

    const resetFilters = () => {
        setPagination((current) => ({ ...current, current: 1 }));
        setFilters({});
    };

    const handlePageChange = (page: number) => {
        setPagination((current) => ({ ...current, current: page }));
    };

    const handlePageSizeChange = (_current: number, size: number) => {
        setPagination((current) => ({
            ...current,
            current: 1,
            pageSize: size,
        }));
    };

    return {
        applyFilters,
        data,
        fetchData,
        filters,
        handlePageChange,
        handlePageSizeChange,
        isLoading,
        pagination,
        resetFilters,
    };
};